import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { format as formatSql } from 'sql-formatter';

import { useCohortTranslationQuery } from '../../api/useCohortTranslationQuery';

/**
 * Pure TanStack-driven view-model for the SQL section. Posts the
 * in-memory cohort definition to `/translate` (via the gen-3
 * useCohortTranslationQuery) and formats the result.
 *
 * Returns:
 *  - `sqlValue`: the formatted SQL string, or '' when the cohort has
 *    no criteria yet (translate isn't called) or has unresolved refs.
 *  - `isLoading`: true on the first fetch only; subsequent edits use
 *    `placeholderData` so the prior SQL stays on screen.
 *  - `hasUnresolvedRefs`: backend short-circuited because the cohort
 *    references a library AV that isn't yet bound. The section should
 *    render an "incomplete" placeholder; Run-cohort should be disabled.
 */
export const useSqlSection = () => {
  const { projectId, cohortId, cohortVersionId } = useParams<{
    projectId: string;
    cohortId: string;
    cohortVersionId: string;
  }>();

  const { data, isLoading } = useCohortTranslationQuery({
    projectId: projectId ?? '',
    cohortId: cohortId ?? '',
    cohortVersionId: cohortVersionId ?? '',
  });

  const rawSql = data?.sqlQuery ?? '';
  const hasUnresolvedRefs = data?.hasUnresolvedRefs ?? false;

  const sqlValue = useMemo(() => {
    if (!rawSql) return '';
    try {
      return formatSql(rawSql, { language: 'snowflake' });
    } catch {
      return rawSql;
    }
  }, [rawSql]);

  return {
    sqlValue,
    isLoading,
    hasUnresolvedRefs,
  };
};
